import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import dashLogo from '../../images/HUGHES-DIGITAL-SIGNAGE_BLUE.png';

interface AcceptInvitationProps {
  actionUrl: string;
  invitationToken: string;
}

export function MssAcceptInvitation({ actionUrl, invitationToken }: AcceptInvitationProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '';
  const inputType = showPassword ? 'text' : 'password';

  return (
    <div className='min-h-screen bg-zinc-50 flex items-center justify-center p-8'>
      <div className='w-full max-w-md'>
        <div className='flex items-center justify-center mb-8'>
          <img src={dashLogo} alt='DashSignage' className='w-full' />
        </div>

        <h1 className='text-xl font-semibold text-zinc-900 mb-2'>Accept your invitation</h1>
        <p className='text-sm text-zinc-500 mb-6'>Set a password to activate your account.</p>

        <form
          action={actionUrl}
          method='post'
          onSubmit={() => setIsLoading(true)}
          className='space-y-5'
        >
          <input type='hidden' name='authenticity_token' value={csrfToken} />
          <input type='hidden' name='_method' value='put' />
          <input type='hidden' name='user[invitation_token]' value={invitationToken} />

          <div className='space-y-1'>
            <Label htmlFor='password' className='text-sm font-normal text-black-950'>Password</Label>
            <div className='relative'>
              <Input
                id='password'
                name='user[password]'
                type={inputType}
                autoFocus
                required
                tabIndex={1}
                className='h-11 pr-10 bg-[#ededef] shadow-none focus-visible:ring-3 focus-visible:ring-zinc-300 focus-visible:border focus-visible:border-ring'
              />
              <button
                type='button'
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className='absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors'
              >
                {showPassword ? <EyeOff className='w-5 h-5' /> : <Eye className='w-5 h-5' />}
              </button>
            </div>
          </div>

          <div className='space-y-1'>
            <Label htmlFor='password_confirmation' className='text-sm font-normal text-black-950'>Password confirmation</Label>
            <Input
              id='password_confirmation'
              name='user[password_confirmation]'
              type={inputType}
              required
              tabIndex={2}
              className='h-11 bg-[#ededef] shadow-none focus-visible:ring-3 focus-visible:ring-zinc-300 focus-visible:border focus-visible:border-ring'
            />
          </div>

          <Button
            type='submit'
            tabIndex={3}
            className='w-full h-11 bg-blue-700 hover:bg-blue-800 transition-colors'
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2' />
                Setting up account...
              </>
            ) : (
              'Set my password'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}

export default MssAcceptInvitation;
